import catchify from 'catchify';
import { omit } from 'lodash-es';

import { sleep } from './helpers';

const defaultHeaders = {};
let userToken = null;

/**
 * Set global userToken and `tgb-token` header if token is not null/undefined.
 * @param {String} token - User token to use for requests as `tgb-token` header.
 */
const setUserToken = (token) => {
  userToken = token;

  if (userToken) {
    defaultHeaders['tgb-token'] = userToken;
  } else {
    // Don't include `tgb-token` if we don't have a token.
    // Some of our auth plugins don't like an undefined header.
    delete defaultHeaders['tgb-token'];
  }
};

const DefaultRequestOpts = (headers = {}) => ({
  credentials: 'include',
  mode: 'cors',
  headers: {
    ...headers,
    ...defaultHeaders,
  },
});

const buildQueryString = (opts = {}) => {
  const str = Object.keys(opts).map((key) => (
    (key && opts[key]) ? `${key}=${encodeURIComponent(opts[key])}` : ''
  )).filter(Boolean).join('&');

  return str ? `?${str}` : '';
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

/**
 * Make an HTTP request using Fetch API.
 * @param {String} url - URL to hit.
 * @param {Object} options - Additional data to merge into request payload
 * @param {Number} attempts - Current number of attempts. Used to retry failed requests.
 * @returns {Object} JSON response from server.
 */
async function requestFetch(url, options, attempts = 0) {
  if (options?.query) {
    /* eslint-disable no-param-reassign */
    url += `${buildQueryString(options.query)}`;
    delete options.query;
  }

  const opts = {
    ...DefaultRequestOpts(options?.headers),
    ...omit(options, ['headers']),
  };

  // Set `Tgb-Owner` header for MONO_API requests only.
  if (url.indexOf(process.env.MONO_API) === 0 && window.vm?.$store?.state?.production?.owner) {
    opts.headers['Tgb-Owner'] = window.vm.$store.state.production.owner;
  }

  const [fetchErr, resp] = await catchify(
    fetch(url, opts),
  );

  // fetchErr = "Failed to Fetch" error (most likely). Possibly badly formed URL.
  if (fetchErr || !resp.ok) {
    const method = options?.method || 'GET';

    // Only retry GET requests. We don't want anything possibly destructive ran over and over.
    // Also don't retry if the status returned is "401 Unauthorized"
    if (attempts < MAX_RETRIES && method === 'GET' && resp?.status !== 401) {
      await sleep(RETRY_DELAY);
      return requestFetch(url, options, attempts + 1);
    }

    if (fetchErr) {
      let error = fetchErr;
      let errType = 'FetchError';

      if (typeof fetchErr?.json === 'function') {
        // Parse JSON error, or fallback to generic error if fails.
        fetchErr.json().then((jsonError) => {
          error = jsonError;
          errType = 'JSONError';
        }).catch((genericError) => {
          error = genericError;
          errType = 'GenericError';
        });
      }

      const err = new Error(`${errType} | ${method} ${url} | ${error?.message || error}`);
      err.type = errType;
      throw err;
    }

    if (resp.status === 401) {
      // Go to login
      window.location = `${process.env.TAGBOARD_ACCOUNT_URL}/signin?return_url=${encodeURIComponent(window.location.href)}`;
      return {}; // Early return stops the error from bubbling up to Sentry
    }

    // Request Error
    const err = new Error(`Request to ${url} failed with status ${resp.status} ${resp.statusText}`);
    err.status = resp.status;
    throw err;
  }

  const contentType = resp.headers.get('content-type');

  if (contentType?.indexOf('application/json') === 0) {
    return resp.status === 204 ? {} : resp.json();
  }

  return resp.status === 204 ? {} : resp.text();
}

export {
  setUserToken,
  requestFetch,
  requestFetch as fetch,
  buildQueryString,
};
